import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useGroupsList } from '@/features/groups/api';
import { useLocationsList } from '@/features/locations/api';
import { useTrunksList } from '@/features/trunks/api';
import { useCreateDialer } from '../api';
import { buildGroupRowsPayload } from '../payload';
import { dialerCreateSchema, type DialerCreateFormInput } from '../schema';

export function CreateDialerPage() {
  const navigate = useNavigate();
  const trunks = useTrunksList();
  const locations = useLocationsList();
  const groups = useGroupsList();
  const create = useCreateDialer();

  const groupOptions = useMemo(
    () =>
      (groups.data ?? []).map((g) => ({
        value: String(g.groupId),
        label: g.groupName,
      })),
    [groups.data],
  );

  const form = useForm<DialerCreateFormInput>({
    resolver: zodResolver(dialerCreateSchema),
    mode: 'onTouched',
    defaultValues: {
      dialerName: '',
      dialerIP: '',
      dialerPort: '',
      trunkId: '',
      locationId: '',
      groupIds: [],
    },
  });

  const onSubmit = (input: DialerCreateFormInput) => {
    create.mutate(
      {
        dialerName: input.dialerName.trim(),
        dialerIP: input.dialerIP.trim(),
        dialerPort: input.dialerPort.trim(),
        trunkId: input.trunkId,
        locationId: input.locationId,
        groupRows: buildGroupRowsPayload(input.groupIds, groupOptions),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Dialer created');
          navigate('/dialer-list');
        },
        onError: (err) =>
          toast.error(readErrorMessage(err) ?? 'Could not create the dialer.'),
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Create Dialer"
        description="Register a new dialer with its trunk, location, and the groups it should serve."
        actions={
          <Button asChild variant="outline">
            <Link to="/dialer-list">
              <ArrowLeft className="h-4 w-4" />
              Back to dialers
            </Link>
          </Button>
        }
      />

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardHeader>
              <CardTitle>Dialer details</CardTitle>
              <CardDescription>
                IP / port for the dialer host and a unique display name.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="dialerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dialer name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. dialer-mum-01" autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dialerIP"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>IP / Host</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 10.0.0.21" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dialerPort"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Port</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 5060" inputMode="numeric" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Routing</CardTitle>
              <CardDescription>
                Choose the trunk and location this dialer belongs to, and the groups it should serve.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="trunkId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trunk</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              trunks.isLoading ? 'Loading trunks…' : 'Select a trunk'
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(trunks.data ?? []).map((t) => (
                          <SelectItem key={t.trunkId} value={String(t.trunkId)}>
                            {t.trunkName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="locationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              locations.isLoading
                                ? 'Loading locations…'
                                : 'Select a location'
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(locations.data ?? []).map((l) => (
                          <SelectItem key={l.locationId} value={String(l.locationId)}>
                            {l.locationName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="groupIds"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Groups</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={groupOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={
                          groups.isLoading ? 'Loading groups…' : 'Pick groups for this dialer'
                        }
                        searchPlaceholder="Filter groups…"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/dialer-list">Cancel</Link>
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create dialer
            </Button>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
